import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { User } from 'firebase/auth';
import { firebaseConfigured, loadAuth } from './firebase';
import { ApiError, getQuota, setAuthTokenProvider } from './api';
import { apiConfigured } from '../config/env';
import type { QuotaStatus } from './types';

/**
 * Firebase Auth with Google sign-in, plus the quota counter that goes with it.
 *
 * Signing in is required to RUN an analysis. It is not required to view a
 * shared map, so nothing here blocks rendering: the provider settles into
 * `signed-out` and the public route carries on. The SDK itself is imported
 * dynamically (see ./firebase.ts), so a visitor who only opens a shared link
 * never downloads it.
 */

export type AuthStatus = 'loading' | 'signed-in' | 'signed-out' | 'unavailable';

export interface AuthState {
  status: AuthStatus;
  user: User | null;
  /** Non-null once a sign-in attempt has failed; cleared on the next attempt. */
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  quota: QuotaStatus | null;
  quotaError: string | null;
  quotaLoading: boolean;
  refreshQuota: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(
    firebaseConfigured ? 'loading' : 'unavailable',
  );
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<QuotaStatus | null>(null);
  const [quotaError, setQuotaError] = useState<string | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaNonce, setQuotaNonce] = useState(0);

  // The API client asks for a token through this hook rather than importing
  // Firebase itself, which keeps the client usable on the public routes.
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  useEffect(() => {
    setAuthTokenProvider(async (forceRefresh = false) => {
      const current = userRef.current;
      if (!current) return null;
      return current.getIdToken(forceRefresh);
    });
  }, []);

  useEffect(() => {
    if (!firebaseConfigured) return;
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    void (async () => {
      try {
        const [{ onIdTokenChanged }, auth] = await Promise.all([
          import('firebase/auth'),
          loadAuth(),
        ]);
        if (cancelled) return;
        // onIdTokenChanged rather than onAuthStateChanged: it also fires on the
        // hourly token refresh, so a long session never sends a stale token.
        unsubscribe = onIdTokenChanged(auth, (next) => {
          setUser(next);
          setStatus(next ? 'signed-in' : 'signed-out');
        });
      } catch (cause) {
        if (cancelled) return;
        setStatus('unavailable');
        setError(cause instanceof Error ? cause.message : String(cause));
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!user || !apiConfigured) {
      setQuota(null);
      setQuotaError(null);
      return;
    }
    const controller = new AbortController();
    setQuotaLoading(true);
    setQuotaError(null);
    getQuota(controller.signal)
      .then((next) => {
        setQuota(next);
        setQuotaError(null);
      })
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.code === 'cancelled') return;
        setQuotaError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => setQuotaLoading(false));
    return () => controller.abort();
  }, [user, quotaNonce]);

  const signIn = useCallback(async () => {
    setError(null);
    if (!firebaseConfigured) {
      setError('VITE_FIREBASE_*');
      return;
    }
    try {
      const [{ GoogleAuthProvider, signInWithPopup, signInWithRedirect }, auth] = await Promise.all([
        import('firebase/auth'),
        loadAuth(),
      ]);
      const provider = new GoogleAuthProvider();
      // Always show the account chooser: a shared lab machine is the norm.
      provider.setCustomParameters({ prompt: 'select_account' });
      try {
        await signInWithPopup(auth, provider);
      } catch (cause: unknown) {
        const code =
          cause && typeof cause === 'object' && 'code' in cause ? String(cause.code) : '';
        // A blocked popup is browser policy, not a failure worth reporting;
        // fall back to the redirect flow instead.
        if (
          code === 'auth/popup-blocked' ||
          code === 'auth/operation-not-supported-in-this-environment'
        ) {
          await signInWithRedirect(auth, provider);
          return;
        }
        // The user closing the popup is not an error either.
        if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return;
        throw cause;
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }, []);

  const signOut = useCallback(async () => {
    if (!firebaseConfigured) return;
    const [{ signOut: fbSignOut }, auth] = await Promise.all([
      import('firebase/auth'),
      loadAuth(),
    ]);
    await fbSignOut(auth);
  }, []);

  const refreshQuota = useCallback(() => setQuotaNonce((n) => n + 1), []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      error,
      signIn,
      signOut,
      quota,
      quotaError,
      quotaLoading,
      refreshQuota,
    }),
    [status, user, error, signIn, signOut, quota, quotaError, quotaLoading, refreshQuota],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
